import { type SetStateAction } from 'react';
import { format, startOfDay, endOfDay, parseISO, setHours, setMinutes, setSeconds } from 'date-fns';
import type { TimePeriodFilterValue } from '@/shared/components/TimePeriodFilter';
import {
  addServerCalendarDays,
  formatServerDate,
  formatServerEndOfDay,
  formatServerStartOfDay,
  getServerCalendarDate,
  getServerWeekEnd,
  getServerWeekStart,
} from '@/shared/utils/serverTimeContext';

/**
 * Derives a date string from a raw ISO datetime string, returning empty string when not available.
 */
const deriveDateFromRaw = (rawDate: string | null | undefined): string => {
  if (!rawDate) return '';
  try { return format(new Date(rawDate), 'yyyy-MM-dd'); } catch { return ''; }
};

/**
 * Derives a 4-digit HHmm time string from a raw ISO datetime string.
 */
const deriveTimeFromRaw = (rawDate: string | null | undefined): string => {
  if (!rawDate) return '';
  try { return format(new Date(rawDate), 'HHmm'); } catch { return ''; }
};

/**
 * Maps the saved range_default_enum value back to the DateRangeFilter's internal period value.
 *
 * Mapping:
 *   Saved 'time'  → DateRangeFilter 'date'       (single-date picker mode)
 *   Saved 'date'  → DateRangeFilter 'date_range'  (date-range picker mode)
 *   Everything else maps 1-to-1 (today, yesterday, current_week, previous_week, last_14_days, '')
 *
 * @param rangeEnum - The stored enum value (from URL params or filterset expression)
 * @param defaultPeriod - The period to use when rangeEnum is undefined (defaults to 'today')
 */
export const enumToInitialPeriod = (
  rangeEnum: string | undefined,
  defaultPeriod = 'today',
): string => {
  if (rangeEnum === undefined) return defaultPeriod;
  if (rangeEnum === 'time') return 'date';
  if (rangeEnum === 'date') return 'date_range';
  return rangeEnum; // includes '' for "No Filters" and named presets
};

/**
 * Maps the DateRangeFilter's internal period value to the value that should be
 * saved as range_default_enum in the URL / filterset expression.
 *
 * Mapping:
 *   'date'       → 'time'
 *   'date_range' → 'date'
 *   Everything else maps 1-to-1
 */
export const periodToEnum = (period: string): string => {
  if (period === 'date') return 'time';
  if (period === 'date_range') return 'date';
  return period;
};

/**
 * Interface for filters that support the date range save/restore pattern.
 * Any filter type that uses DateRangeFilter with save support should include these fields.
 */
export interface DateRangeFilterFields {
  from?: string | null;
  to?: string | null;
  range_default_enum?: string;
  range_default_date?: string;
  range_default_date_from?: string;
  range_default_date_to?: string;
  range_default_time_from?: string;
  range_default_time_to?: string;
}

/**
 * Builds the date range portion of sanitizedCurrentFilters.
 * Excludes raw from/to timestamps — only the range_default_* fields needed to
 * reconstruct the date range period are included.
 *
 * @param filters - The current filter state (must include range_default_* fields)
 * @param ignoreTimeForDateRange - When true, time fields are excluded from the saved filters for date_range mode
 */
export const buildSanitizedDateRangeFilters = (
  filters: DateRangeFilterFields,
  ignoreTimeForDateRange = false,
): Partial<DateRangeFilterFields> => {
  const isTodayYesterday = filters.range_default_enum === 'today' || filters.range_default_enum === 'yesterday';

  // For 'time' mode: fall back to deriving from filters.from/to when range_default_* fields are empty
  const effectiveDate = filters.range_default_enum === 'time'
    ? (filters.range_default_date || deriveDateFromRaw(filters.from))
    : filters.range_default_date;
  const effectiveTimeFrom = (filters.range_default_enum === 'time' || isTodayYesterday)
    ? (filters.range_default_time_from || deriveTimeFromRaw(filters.from))
    : filters.range_default_time_from;
  const effectiveTimeTo = (filters.range_default_enum === 'time' || isTodayYesterday)
    ? (filters.range_default_time_to || deriveTimeFromRaw(filters.to))
    : filters.range_default_time_to;

  // For 'date' (date_range) mode: fall back to deriving from filters.from/to when range_default_* fields are empty
  const effectiveDateFrom = filters.range_default_enum === 'date'
    ? (filters.range_default_date_from || deriveDateFromRaw(filters.from))
    : filters.range_default_date_from;
  const effectiveDateTo = filters.range_default_enum === 'date'
    ? (filters.range_default_date_to || deriveDateFromRaw(filters.to))
    : filters.range_default_date_to;
  const effectiveDateRangeTimeFrom = filters.range_default_enum === 'date'
    ? (filters.range_default_time_from || deriveTimeFromRaw(filters.from))
    : filters.range_default_time_from;
  const effectiveDateRangeTimeTo = filters.range_default_enum === 'date'
    ? (filters.range_default_time_to || deriveTimeFromRaw(filters.to))
    : filters.range_default_time_to;

  return {
    ...(filters.range_default_enum ? { range_default_enum: filters.range_default_enum } : {}),
    ...(filters.range_default_enum === 'time' && effectiveDate
      ? { range_default_date: effectiveDate }
      : {}),
    // time fields for single-date ('time') mode
    ...(filters.range_default_enum === 'time' && effectiveTimeFrom
      ? { range_default_time_from: effectiveTimeFrom }
      : {}),
    ...(filters.range_default_enum === 'time' && effectiveTimeTo
      ? { range_default_time_to: effectiveTimeTo }
      : {}),
    ...(filters.range_default_enum === 'date' && effectiveDateFrom
      ? { range_default_date_from: effectiveDateFrom }
      : {}),
    ...(filters.range_default_enum === 'date' && effectiveDateTo
      ? { range_default_date_to: effectiveDateTo }
      : {}),
    // time fields for date-range ('date') mode — excluded when ignoreTimeForDateRange is true
    ...(!ignoreTimeForDateRange && filters.range_default_enum === 'date' && effectiveDateRangeTimeFrom
      ? { range_default_time_from: effectiveDateRangeTimeFrom }
      : {}),
    ...(!ignoreTimeForDateRange && filters.range_default_enum === 'date' && effectiveDateRangeTimeTo
      ? { range_default_time_to: effectiveDateRangeTimeTo }
      : {}),
    // time fields for today/yesterday presets (allow saving custom time on filter save)
    ...(isTodayYesterday && effectiveTimeFrom
      ? { range_default_time_from: effectiveTimeFrom }
      : {}),
    ...(isTodayYesterday && effectiveTimeTo
      ? { range_default_time_to: effectiveTimeTo }
      : {}),
  };
};

/**
 * Creates a setDates callback for use with DateRangeFilter.
 * Syncs the from/to filter fields and also keeps range_default_date* in sync
 * when in 'date' (single-date) or 'date_range' mode.
 *
 * @param params.filters - Current filter state
 * @param params.onFilterChange - Filter change handler
 * @param params.dateTimeFormat - Format string for from/to values (default: "yyyy-MM-dd'T'HH:mm:ss")
 * @param params.ignoreTimeForDateRange - When true, strips time from date_range dates and clears time filter fields
 */
export const createSetDates = <T extends DateRangeFilterFields>(params: {
  filters: T;
  onFilterChange: (key: keyof T, value: any) => void;
  dateTimeFormat?: string;
  ignoreTimeForDateRange?: boolean;
}) => {
  const { filters, onFilterChange, dateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss", ignoreTimeForDateRange = false } = params;

  return (newDatesAction: SetStateAction<{ from: Date | null; to: Date | null }>) => {
    const currentDates = {
      from: filters.from ? new Date(filters.from) : null,
      to: filters.to ? new Date(filters.to) : null,
    };
    const newDates =
      typeof newDatesAction === 'function' ? newDatesAction(currentDates) : newDatesAction;

    const currentEnum = filters.range_default_enum || '';

    // When ignoring time for date range, force start/end of day
    const effectiveFrom = (ignoreTimeForDateRange && currentEnum === 'date' && newDates.from)
      ? startOfDay(newDates.from)
      : newDates.from;
    const effectiveTo = (ignoreTimeForDateRange && currentEnum === 'date' && newDates.to)
      ? endOfDay(newDates.to)
      : newDates.to;

    onFilterChange('from' as keyof T, effectiveFrom ? format(effectiveFrom, dateTimeFormat) : '');
    onFilterChange('to' as keyof T, effectiveTo ? format(effectiveTo, dateTimeFormat) : '');

    // Keep range_default_date / range_default_date_from / range_default_date_to in sync
    // Times are stored as 4-digit strings (HHmm) e.g. "0105", "2355"
    if (currentEnum === 'today' || currentEnum === 'yesterday') {
      // Save custom time selections for today/yesterday presets
      if (newDates.from) {
        onFilterChange('range_default_time_from' as keyof T, format(newDates.from, 'HHmm'));
      }
      if (newDates.to) {
        onFilterChange('range_default_time_to' as keyof T, format(newDates.to, 'HHmm'));
      }
    } else if (currentEnum === 'time') {
      if (newDates.from) {
        onFilterChange('range_default_date' as keyof T, format(newDates.from, 'yyyy-MM-dd'));
        onFilterChange('range_default_time_from' as keyof T, format(newDates.from, 'HHmm'));
      }
      if (newDates.to) {
        onFilterChange('range_default_time_to' as keyof T, format(newDates.to, 'HHmm'));
      }
    } else if (currentEnum === 'date') {
      if (ignoreTimeForDateRange) {
        // Only store date portion, clear time fields so they are never saved/applied to URL
        onFilterChange('range_default_date_from' as keyof T, newDates.from ? format(newDates.from, 'yyyy-MM-dd') : '');
        onFilterChange('range_default_date_to' as keyof T, newDates.to ? format(newDates.to, 'yyyy-MM-dd') : '');
        onFilterChange('range_default_time_from' as keyof T, '');
        onFilterChange('range_default_time_to' as keyof T, '');
      } else {
        if (newDates.from) {
          onFilterChange('range_default_date_from' as keyof T, format(newDates.from, 'yyyy-MM-dd'));
          onFilterChange('range_default_time_from' as keyof T, format(newDates.from, 'HHmm'));
        }
        if (newDates.to) {
          onFilterChange('range_default_date_to' as keyof T, format(newDates.to, 'yyyy-MM-dd'));
          onFilterChange('range_default_time_to' as keyof T, format(newDates.to, 'HHmm'));
        }
      }
    }
  };
};

/**
 * Creates a handlePeriodChange callback for use with DateRangeFilter's onPeriodChange prop.
 * Updates range_default_enum and clears stale date fields when the period dropdown changes.
 *
 * @param onFilterChange - Filter change handler
 */
export const createHandlePeriodChange = <T extends DateRangeFilterFields>(
  onFilterChange: (key: keyof T, value: any) => void,
) => {
  return (period: string) => {
    const enumValue = periodToEnum(period);
    onFilterChange('range_default_enum' as keyof T, enumValue);

    // Clear date fields that are not relevant for the new mode
    if (period !== 'date_range') {
      onFilterChange('range_default_date_from' as keyof T, '');
      onFilterChange('range_default_date_to' as keyof T, '');
    }
    if (period !== 'date') {
      onFilterChange('range_default_date' as keyof T, '');
    }
    // Clear time fields when switching away from both date pickers
    if (period !== 'date_range' && period !== 'date') {
      onFilterChange('range_default_time_from' as keyof T, '');
      onFilterChange('range_default_time_to' as keyof T, '');
    }
  };
};
/**
 * Computes the from/to datetime strings for a given range_default_enum preset value.
 * Returns null if the enum requires explicit date inputs ('time' / 'date' modes).
 *
 * @param rangeEnum - The stored enum value (e.g. 'today', 'yesterday', 'current_week', etc.)
 * @param dateTimeFormat - Format string for from/to values (default: "yyyy-MM-dd'T'HH:mm:ss")
 */
export const computeFromToForEnum = (
  rangeEnum: string,
  dateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss",
): { from: string; to: string } | null => {
  const today = getServerCalendarDate();

  const serverRange = (fromDate: { year: number; month: number; day: number }, toDate?: { year: number; month: number; day: number } | null) => {
    const from = formatServerStartOfDay(fromDate);
    const to = toDate ? formatServerEndOfDay(toDate) : '';
    return dateTimeFormat === "yyyy-MM-dd'T'HH:mm:ss"
      ? { from, to }
      : {
          from: format(new Date(from), dateTimeFormat),
          to: to ? format(new Date(to), dateTimeFormat) : '',
        };
  };

  switch (rangeEnum) {
    case 'today':
      return serverRange(today, today);
    case 'yesterday':
      return serverRange(addServerCalendarDays(today, -1), addServerCalendarDays(today, -1));
    case 'current_week':
      // Rails: today.beginning_of_week … today.end_of_week.end_of_day (full Mon–Sun)
      // See model_search_range_support.rb resolve_date_enum_range SF_RANGE_CURRENT_WEEK
      return serverRange(getServerWeekStart(today), getServerWeekEnd(today));
    case 'previous_week': {
      const previousWeekStart = addServerCalendarDays(getServerWeekStart(today), -7);
      return serverRange(previousWeekStart, getServerWeekEnd(previousWeekStart));
    }
    // Legacy Rails behavior (TripEvent::OPEN_RANGE_SEARCH_DAYS = 7):
    //   resolve_sf_range_default_open_date_range → [EcoTime.today - 7.days, nil]
    //   → query: ["gte", "timestamp", "<7-days-ago-start-of-day>"]  (no upper bound)
    // This is an open-ended range — only a lower bound is applied.
    case 'last_7_days':
      return serverRange(addServerCalendarDays(today, -7), null);
    case 'last_14_days':
      return serverRange(addServerCalendarDays(today, -13), today);
    case 'last_31_days':
      // Open-ended query from 31 days ago (32 inclusive days, no upper bound).
      // Matches legacy API: ["gte","timestamp","<today - 31 days>"] with no "lte".
      return serverRange(addServerCalendarDays(today, -31), null);
    case 'tomorrow':
      return serverRange(addServerCalendarDays(today, 1), addServerCalendarDays(today, 1));
    case 'next_week': {
      const nextWeekStart = addServerCalendarDays(getServerWeekStart(today), 7);
      return serverRange(nextWeekStart, getServerWeekEnd(nextWeekStart));
    }
    case 'from_today':
      return serverRange(today, null);
    default:
      return null;
  }
};

export const getDateOnlyRangeForEnum = (
  rangeEnum: string,
): { fromDate: string; toDate?: string } | null => {
  const computed = computeFromToForEnum(rangeEnum);
  if (!computed) return null;

  return {
    fromDate: computed.from.slice(0, 10),
    ...(computed.to ? { toDate: computed.to.slice(0, 10) } : {}),
  };
};

export const getServerTodayDateString = (): string =>
  formatServerDate(getServerCalendarDate());

/**
 * Serialises the range_default_* fields of a filter object into URLSearchParams entries.
 * Call this inside a custom `filterToParams` serializer to add the date range portion.
 *
 * `from` and `to` are intentionally NOT included — they are recomputed on re-apply
 * via `applyDateRangeFromParams`.
 *
 * @param filters - The filter state (must include range_default_* fields)
 * @param params  - The URLSearchParams instance to append to
 * @param ignoreTimeForDateRange - When true, time fields are excluded from URL params for date_range mode
 */
export const appendDateRangeParams = (
  filters: DateRangeFilterFields,
  params: URLSearchParams,
  ignoreTimeForDateRange = false,
): void => {
  const { range_default_enum } = filters;
  const isTodayYesterday = range_default_enum === 'today' || range_default_enum === 'yesterday';

  // For 'time' mode: fall back to deriving from filters.from/to when range_default_* fields are empty
  const effectiveDate = range_default_enum === 'time'
    ? (filters.range_default_date || deriveDateFromRaw(filters.from))
    : filters.range_default_date;
  const effectiveTimeFrom = (range_default_enum === 'time' || isTodayYesterday)
    ? (filters.range_default_time_from || deriveTimeFromRaw(filters.from))
    : filters.range_default_time_from;
  const effectiveTimeTo = (range_default_enum === 'time' || isTodayYesterday)
    ? (filters.range_default_time_to || deriveTimeFromRaw(filters.to))
    : filters.range_default_time_to;

  // For 'date' (date_range) mode: fall back to deriving from filters.from/to when range_default_* fields are empty
  const effectiveDateFrom = range_default_enum === 'date'
    ? (filters.range_default_date_from || deriveDateFromRaw(filters.from))
    : filters.range_default_date_from;
  const effectiveDateTo = range_default_enum === 'date'
    ? (filters.range_default_date_to || deriveDateFromRaw(filters.to))
    : filters.range_default_date_to;
  const effectiveDateRangeTimeFrom = range_default_enum === 'date'
    ? (filters.range_default_time_from || deriveTimeFromRaw(filters.from))
    : filters.range_default_time_from;
  const effectiveDateRangeTimeTo = range_default_enum === 'date'
    ? (filters.range_default_time_to || deriveTimeFromRaw(filters.to))
    : filters.range_default_time_to;

  if (range_default_enum === 'time' && effectiveDate) {
    params.set('q.range_default_date', effectiveDate);
  }
  // time fields for single-date ('time') mode
  if (range_default_enum === 'time' && effectiveTimeFrom) {
    params.set('q.range_default_time_from', effectiveTimeFrom);
  }
  if (range_default_enum === 'time' && effectiveTimeTo) {
    params.set('q.range_default_time_to', effectiveTimeTo);
  }
  if (range_default_enum === 'date' && effectiveDateFrom) {
    params.set('q.range_default_date_from', effectiveDateFrom);
  }
  if (range_default_enum === 'date' && effectiveDateTo) {
    params.set('q.range_default_date_to', effectiveDateTo);
  }
  // time fields for date-range ('date') mode — excluded when ignoreTimeForDateRange is true
  if (!ignoreTimeForDateRange && range_default_enum === 'date' && effectiveDateRangeTimeFrom) {
    params.set('q.range_default_time_from', effectiveDateRangeTimeFrom);
  }
  if (!ignoreTimeForDateRange && range_default_enum === 'date' && effectiveDateRangeTimeTo) {
    params.set('q.range_default_time_to', effectiveDateRangeTimeTo);
  }
  // time fields for today/yesterday presets
  if (isTodayYesterday && effectiveTimeFrom) {
    params.set('q.range_default_time_from', effectiveTimeFrom);
  }
  if (isTodayYesterday && effectiveTimeTo) {
    params.set('q.range_default_time_to', effectiveTimeTo);
  }
  // Emit range_default_enum only for named presets that need to be reconstructed on
  // re-apply (today, yesterday, current_week, previous_week) and for the picker modes
  // (time, date). For empty (No Filters), last_7_days and last_31_days we skip it —
  // these values are computed dynamically on each page load and don't need to be
  // round-tripped through the URL.
  if (
    range_default_enum &&
    range_default_enum !== 'last_7_days' &&
    range_default_enum !== 'last_31_days'
  ) {
    params.set('q.range_default_enum', range_default_enum);
  }
};

/**
 * Reconstructs `from` and `to` from the range_default_* fields of a partially-parsed
 * filter object. Call this inside a custom `paramsToFilter` deserializer after basic
 * param parsing to restore the date range.
 *
 * @param raw            - Partial filter already parsed from URL params
 * @param dateTimeFormat - Format string for from/to values (default: "yyyy-MM-dd'T'HH:mm:ss")
 * @returns              - The same object augmented with from/to when reconstructable
 */
export const applyDateRangeFromParams = <T extends DateRangeFilterFields>(
  raw: Partial<T>,
  dateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss",
): Partial<T> => {
  const rangeEnum = raw.range_default_enum || '';

  if (!rangeEnum) return raw;

  // Enum-based preset (today, yesterday, current_week, previous_week, last_14_days)
  const computed = computeFromToForEnum(rangeEnum, dateTimeFormat);
  if (computed) {
    // For today/yesterday, apply saved custom time fields if present
    if ((rangeEnum === 'today' || rangeEnum === 'yesterday') && (raw.range_default_time_from || raw.range_default_time_to)) {
      const parseHHmmLocal = (s: string): [number, number] => {
        const h = parseInt(s.substring(0, 2), 10);
        const m = parseInt(s.substring(2, 4), 10);
        return [isNaN(h) ? 0 : h, isNaN(m) ? 0 : m];
      };
      // Determine the target date (today or yesterday)
      const targetFrom = new Date(computed.from);
      const targetTo = new Date(computed.to);
      let fromDate = startOfDay(targetFrom);
      let toDate = endOfDay(targetTo);
      if (raw.range_default_time_from) {
        const [hFrom, mFrom] = parseHHmmLocal(raw.range_default_time_from);
        fromDate = setSeconds(setMinutes(setHours(targetFrom, hFrom), mFrom), 0);
      }
      if (raw.range_default_time_to) {
        const [hTo, mTo] = parseHHmmLocal(raw.range_default_time_to);
        toDate = setSeconds(setMinutes(setHours(targetTo, hTo), mTo), 0);
      }
      return {
        ...raw,
        from: format(fromDate, dateTimeFormat),
        to: format(toDate, dateTimeFormat),
      };
    }
    return { ...raw, from: computed.from, to: computed.to };
  }

  /**
   * Parses a 4-digit time string (HHmm, e.g. "0105" → h=1, m=5)
   * into hours and minutes.
   */
  const parseHHmm = (s: string): [number, number] => {
    const h = parseInt(s.substring(0, 2), 10);
    const m = parseInt(s.substring(2, 4), 10);
    return [isNaN(h) ? 0 : h, isNaN(m) ? 0 : m];
  };

  // 'time' mode: single-date picker — restore from/to using range_default_date.
  // If time fields are present (4-digit HHmm), use them to preserve the exact time range.
  if (rangeEnum === 'time' && raw.range_default_date) {
    const date = parseISO(raw.range_default_date);
    const timeFromStr = raw.range_default_time_from || '';
    const timeToStr   = raw.range_default_time_to   || '';

    let fromDate = startOfDay(date);
    let toDate   = endOfDay(date);

    if (timeFromStr) {
      const [hFrom, mFrom] = parseHHmm(timeFromStr);
      fromDate = setSeconds(setMinutes(setHours(date, hFrom), mFrom), 0);
    }
    if (timeToStr) {
      const [hTo, mTo] = parseHHmm(timeToStr);
      toDate = setSeconds(setMinutes(setHours(date, hTo), mTo), 0);
    }

    return {
      ...raw,
      from: format(fromDate, dateTimeFormat),
      to:   format(toDate,   dateTimeFormat),
    };
  }

  // 'date' mode: date-range picker — restore from/to from range_default_date_from / _to
  // If time fields are present (4-digit HHmm), use them to preserve the exact time selected.
  if (rangeEnum === 'date') {
    const fromStr     = raw.range_default_date_from || '';
    const toStr       = raw.range_default_date_to   || '';
    const timeFromStr = raw.range_default_time_from  || '';
    const timeToStr   = raw.range_default_time_to    || '';
    if (fromStr && toStr) {
      let fromDate = startOfDay(parseISO(fromStr));
      let toDate   = endOfDay(parseISO(toStr));

      if (timeFromStr) {
        const [hFrom, mFrom] = parseHHmm(timeFromStr);
        fromDate = setSeconds(setMinutes(setHours(parseISO(fromStr), hFrom), mFrom), 0);
      }
      if (timeToStr) {
        const [hTo, mTo] = parseHHmm(timeToStr);
        toDate = setSeconds(setMinutes(setHours(parseISO(toStr), hTo), mTo), 0);
      }

      return {
        ...raw,
        from: format(fromDate, dateTimeFormat),
        to:   format(toDate,   dateTimeFormat),
      };
    }
  }

  return raw;
};

// ─────────────────────────────────────────────────────────────────────────────
// TimePeriodFilterValue-based utilities (used by DateRangeFilterV2 consumers)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Computes from/to datetime strings from a TimePeriodFilterValue.
 *
 * - For preset periods (today, yesterday, current_week, etc.) delegates to computeFromToForEnum.
 * - For 'date' mode: applies fromTime/toTime on the selected date; defaults to start/end of day.
 * - For 'date_range' mode: applies fromTime/toTime on fromDate/toDate; defaults to start/end of day.
 * - For 'no_filter' or missing data: returns { from: '', to: '' }.
 *
 * @param tp             - The TimePeriodFilterValue from the filter state.
 * @param dateTimeFormat - Output format string (default: "yyyy-MM-dd'T'HH:mm:ss").
 */
export const computeFromToForTimePeriod = (
  tp: TimePeriodFilterValue | undefined | null,
  dateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss",
): { from: string; to: string } => {
  if (!tp || tp.period === 'no_filter') {
    return { from: '', to: '' };
  }

  if (tp.period === 'date' && tp.date) {
    const date = parseISO(tp.date);
    let fromDate = startOfDay(date);
    let toDate   = endOfDay(date);
    if (tp.fromTime) {
      const [h, m] = tp.fromTime.split(':').map(Number);
      fromDate = setSeconds(setMinutes(setHours(date, h), m), 0);
    }
    if (tp.toTime) {
      const [h, m] = tp.toTime.split(':').map(Number);
      toDate = setSeconds(setMinutes(setHours(date, h), m), 0);
    }
    return { from: format(fromDate, dateTimeFormat), to: format(toDate, dateTimeFormat) };
  }

  if (tp.period === 'date_range' && tp.fromDate && tp.toDate) {
    const fromDateBase = parseISO(tp.fromDate);
    const toDateBase   = parseISO(tp.toDate);
    // Time fields are not considered for date_range — always use start/end of day.
    const fromDate = startOfDay(fromDateBase);
    const toDate   = endOfDay(toDateBase);
    return { from: format(fromDate, dateTimeFormat), to: format(toDate, dateTimeFormat) };
  }

  // Preset period (today, yesterday, current_week, previous_week, last_14_days, …)
  // Time overrides (fromTime/toTime) are only honoured for single-day presets
  // (today, yesterday) because those represent a specific 24-hour window where a
  // custom sub-range makes sense.  Multi-day presets (current_week, previous_week,
  // last_14_days, last_7_days, last_31_days, etc.) always span the full start/end
  // of their respective date boundaries — carrying over a time from a previous
  // "Date" or "Today" selection would silently narrow the range in a confusing way.
  const SINGLE_DAY_PRESETS = new Set(['today', 'yesterday']);
  const computed = computeFromToForEnum(tp.period, dateTimeFormat);
  if (computed) {
    if (SINGLE_DAY_PRESETS.has(tp.period) && (tp.fromTime || tp.toTime)) {
      const fromBase = new Date(computed.from);
      const toBase   = new Date(computed.to);
      let fromDate = fromBase;
      let toDate   = toBase;
      if (tp.fromTime) {
        const [h, m] = tp.fromTime.split(':').map(Number);
        fromDate = setSeconds(setMinutes(setHours(fromBase, h), m), 0);
      }
      if (tp.toTime) {
        const [h, m] = tp.toTime.split(':').map(Number);
        toDate = setSeconds(setMinutes(setHours(toBase, h), m), 0);
      }
      return { from: format(fromDate, dateTimeFormat), to: format(toDate, dateTimeFormat) };
    }
    return computed;
  }

  return { from: '', to: '' };
};

/**
 * Serialises a TimePeriodFilterValue into URLSearchParams entries using the
 * range_default_* param conventions established by useVehicleEvents.
 *
 * Param mapping:
 *   period 'date'       → q.range_default_enum='time'  + q.range_default_date
 *   period 'date_range' → q.range_default_enum='date'  + q.range_default_date_from/to
 *   period 'no_filter'  → nothing emitted
 *   preset periods      → q.range_default_enum=<period> (except last_7_days / last_31_days → omitted)
 *   fromTime/toTime     → q.range_default_time_from/to as 4-digit HHmm
 *
 * @param tp     - The TimePeriodFilterValue to serialise.
 * @param params - The URLSearchParams instance to append to.
 */
export const appendTimePeriodParams = (
  tp: TimePeriodFilterValue | undefined | null,
  params: URLSearchParams,
): void => {
  if (!tp || tp.period === 'no_filter') return;

  // Dynamic presets that should not be written to the URL so they always
  // recompute to the current rolling window on page reload.
  if (tp.period === 'last_7_days' || tp.period === 'last_31_days') return;

  const enumValue = periodToEnum(tp.period);
  params.set('q.range_default_enum', enumValue);

  if (tp.period === 'date' && tp.date) {
    params.set('q.range_default_date', tp.date);
  } else if (tp.period === 'date_range') {
    if (tp.fromDate) params.set('q.range_default_date_from', tp.fromDate);
    if (tp.toDate)   params.set('q.range_default_date_to',   tp.toDate);
    // Time is not applicable for date_range — omit even if carried in UI state
  }

  // Time fields stored as 4-digit HHmm (strip the colon).
  // Only honoured for single-day periods (today, yesterday) and single-date picker
  // ('date' mode). Multi-day presets (current_week, previous_week, last_14_days, etc.)
  // and date_range always use full start/end-of-day boundaries — writing time params
  // for those would cause them to persist in the URL and silently carry over when the
  // user later switches back to a multi-day preset.
  const SINGLE_DAY_PERIODS = new Set(['today', 'yesterday', 'date']);
  if (SINGLE_DAY_PERIODS.has(tp.period)) {
    if (tp.fromTime) params.set('q.range_default_time_from', tp.fromTime.replace(':', ''));
    if (tp.toTime)   params.set('q.range_default_time_to',   tp.toTime.replace(':', ''));
  }
};

/**
 * Reconstructs a TimePeriodFilterValue from URLSearchParams that were written
 * by appendTimePeriodParams (or the equivalent vehicleEventsFilterToParams pattern).
 *
 * Reverse mapping:
 *   q.range_default_enum absent     → defaultPeriod (typically 'no_filter' or a named preset)
 *   q.range_default_enum = ''       → 'no_filter'
 *   q.range_default_enum = 'time'   → period: 'date'       + date
 *   q.range_default_enum = 'date'   → period: 'date_range' + fromDate/toDate
 *   other preset values             → period: <that value>
 *
 * Time params (4-digit HHmm) are parsed back to HH:mm strings.
 *
 * @param params        - The URLSearchParams to read from (using the q.* prefix keys).
 * @param defaultPeriod - Period to use when no range_default_enum param is present.
 */
export const parseTimePeriodFromParams = (
  params: URLSearchParams,
  defaultPeriod = 'no_filter',
): TimePeriodFilterValue => {
  const parseHHmm = (s: string): string => {
    if (!s || s.length < 4) return '';
    return `${s.substring(0, 2)}:${s.substring(2, 4)}`;
  };

  const rangeEnum = params.get('q.range_default_enum');

  // No range param present → use the caller-supplied default
  if (rangeEnum === null) {
    return { period: defaultPeriod };
  }

  const fromTime = parseHHmm(params.get('q.range_default_time_from') ?? '');
  const toTime   = parseHHmm(params.get('q.range_default_time_to')   ?? '');

  const period = enumToInitialPeriod(rangeEnum, defaultPeriod);

  // Both 'no_filter' and '' (empty enum) indicate no date constraint.
  if (period === 'no_filter' || period === '') {
    return { period: 'no_filter' };
  }

  if (period === 'date') {
    const date = params.get('q.range_default_date') ?? '';
    return { period, date, ...(fromTime && { fromTime }), ...(toTime && { toTime }) };
  }

  if (period === 'date_range') {
    const fromDate = params.get('q.range_default_date_from') ?? '';
    const toDate   = params.get('q.range_default_date_to')   ?? '';
    return { period, fromDate, toDate, ...(fromTime && { fromTime }), ...(toTime && { toTime }) };
  }

  // Preset periods (today, yesterday, current_week, previous_week, last_14_days, …)
  // Time overrides only make sense for single-day presets. Strip them for multi-day
  // ranges so stale time params from a previous 'date'/'today' selection are ignored.
  const SINGLE_DAY_PERIODS_PARSE = new Set(['today', 'yesterday']);
  if (SINGLE_DAY_PERIODS_PARSE.has(period)) {
    return { period, ...(fromTime && { fromTime }), ...(toTime && { toTime }) };
  }
  return { period };
};
